import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Trash2,
  Clock,
  Coffee,
  User,
  Calendar as CalendarIcon,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { colorStyles, fmtHours, HU_DAY_LABELS } from "@/pages/schedule/helpers";
import ConfirmDialog from "@/pages/schedule/ConfirmDialog";

function calcDuration(startTime, endTime, breakMinutes) {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  let s = sh * 60 + sm;
  let e = eh * 60 + em;
  if (e <= s) e += 24 * 60;
  return Math.max(0, e - s - (Number(breakMinutes) || 0)) / 60;
}

export default function ShiftEditDialog({ open, onOpenChange, shift, onSaved }) {
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [unpaidBreak, setUnpaidBreak] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !shift) return;
    setStartTime(shift.start_time || "");
    setEndTime(shift.end_time || "");
    setUnpaidBreak(shift.unpaid_break_minutes || 0);
    setNotes(shift.notes || "");
  }, [open, shift]);

  const duration = useMemo(
    () => calcDuration(startTime, endTime, unpaidBreak),
    [startTime, endTime, unpaidBreak],
  );

  if (!shift) return null;

  const cs = colorStyles(shift.color);
  const dt = new Date((shift.date || "1970-01-01") + "T00:00:00");
  const dayLabel = HU_DAY_LABELS[(dt.getDay() + 6) % 7].long;
  const dateLabel = `${dt.getFullYear()}. ${String(dt.getMonth() + 1).padStart(2, "0")}. ${String(dt.getDate()).padStart(2, "0")}.`;

  const onSubmit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/shifts/${shift._id}`, {
        start_time: startTime,
        end_time: endTime,
        unpaid_break_minutes: Math.max(0, Number(unpaidBreak) || 0),
        notes: notes.trim() || null,
      });
      toast.success("Műszak frissítve.");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/shifts/${shift._id}`);
      toast.success("Műszak törölve.");
      onSaved?.({ deleted: true, _id: shift._id });
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Törlés sikertelen.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="overflow-hidden p-0 sm:max-w-md"
          data-testid="shift-edit-dialog"
        >
          {/* HERO */}
          <div className={cn("relative overflow-hidden px-6 pb-5 pt-6", cs.block)}>
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:14px_14px]"
              aria-hidden
            />
            <div className="relative">
              <div className="flex items-center justify-between">
                <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] opacity-70">
                  <Sparkles className="h-3 w-3" />
                  Műszak szerkesztése
                </p>
                {shift.published && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-500/25 dark:text-emerald-200">
                    <CheckCircle2 className="h-2.5 w-2.5" />
                    Publikálva
                  </span>
                )}
              </div>
              <h2 className="mt-1.5 truncate text-xl font-semibold tracking-tight">
                {shift.template_name || "Műszak"}
              </h2>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 font-mono text-[12px] tabular-nums">
                <span className="opacity-80">{startTime || shift.start_time}–{endTime || shift.end_time}</span>
                {Number(unpaidBreak) > 0 && (
                  <span className="opacity-60">−{unpaidBreak}m szünet</span>
                )}
                <span className="ml-auto rounded-full bg-foreground/[0.08] px-2 py-0.5 text-[11px] font-semibold opacity-90">
                  {fmtHours(duration)}h
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                <span className="inline-flex items-center gap-1 opacity-75">
                  <User className="h-3 w-3" /> {shift.employee_name}
                </span>
                <span className="inline-flex items-center gap-1 opacity-75">
                  <CalendarIcon className="h-3 w-3" /> {dayLabel} · {dateLabel}
                </span>
              </div>
            </div>
          </div>

          {/* BODY */}
          <div className="space-y-5 px-6 py-5">
            {/* Idő-tartomány */}
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <Clock className="-mt-0.5 mr-1 inline h-3 w-3" />
                Idő-tartomány
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <TimeBlock label="Kezdés" id="sh-start" value={startTime} onChange={setStartTime} testId="sh-start" />
                <TimeBlock label="Vége" id="sh-end" value={endTime} onChange={setEndTime} testId="sh-end" />
              </div>
            </div>

            {/* Szünet */}
            <div className="space-y-1.5">
              <Label htmlFor="sh-break" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <Coffee className="-mt-0.5 mr-1 inline h-3 w-3" />
                Fizetetlen szünet
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                {[0, 15, 30, 45, 60].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setUnpaidBreak(m)}
                    className={cn(
                      "rounded-md border px-2.5 py-1 font-mono text-[11px] font-semibold tabular-nums transition-all",
                      Number(unpaidBreak) === m
                        ? "border-foreground bg-foreground text-background"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-border dark:bg-card",
                    )}
                    data-testid={`sh-break-${m}`}
                  >
                    {m === 0 ? "Nincs" : `${m}m`}
                  </button>
                ))}
                <Input
                  id="sh-break"
                  type="number"
                  min={0}
                  step={5}
                  value={unpaidBreak}
                  onChange={(e) => setUnpaidBreak(e.target.value)}
                  className="h-8 w-20 text-center font-mono text-[12px]"
                  placeholder="perc"
                  data-testid="sh-break"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label htmlFor="sh-notes" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Megjegyzés (opcionális)
              </Label>
              <Textarea
                id="sh-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Pl. fontos megjegyzés a műszakhoz"
                className="resize-none text-[13px]"
                data-testid="sh-notes"
              />
            </div>
          </div>

          <DialogFooter className="flex items-center border-t border-slate-100 bg-slate-50/60 px-6 py-3 dark:border-border dark:bg-card sm:justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={deleting || saving}
              className="text-rose-600 hover:bg-rose-500/10 hover:text-rose-700"
              data-testid="sh-delete"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Törlés
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
                Mégse
              </Button>
              <Button onClick={onSubmit} disabled={saving} data-testid="sh-save">
                {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Mentés
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Műszak törlése"
        description={
          <>
            Biztosan törölni szeretnéd <b>{shift.employee_name}</b> műszakját
            {" "}({shift.template_name} · {shift.date})? A művelet nem visszavonható.
          </>
        }
        confirmLabel="Igen, törlés"
        cancelLabel="Mégse"
        onConfirm={onDelete}
        loading={deleting}
        variant="danger"
        testId="sh-delete-confirm"
      />
    </>
  );
}

function TimeBlock({ label, id, value, onChange, testId }) {
  return (
    <label
      htmlFor={id}
      className="group flex cursor-text flex-col rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:border-slate-300 focus-within:border-foreground focus-within:ring-1 focus-within:ring-foreground/10 dark:border-border dark:bg-card"
    >
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>
      <Input
        id={id}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 border-0 p-0 font-mono text-[15px] font-semibold tabular-nums shadow-none focus-visible:ring-0"
        data-testid={testId}
      />
    </label>
  );
}
